/**
 * <TutorStudentChips> — la puce d'un élève du live, posée sur sa vidéo (molecule).
 *
 * Daily du 04/11 (Wajih) : **le signal le plus urgent reprend la place du chrono**, comme sur l'ancienne
 * pastille, avec sa priorité et son code couleur (`tutorStudentSignalChip`) : la main levée en jaune plein,
 * une vraie alerte (hors appli, silence) en rouge plein qui clignote (`<Blink>`), le message non lu, puis le
 * champion, la photo, le rendu. Le weekly du 03/11 (W9) avait réduit ces signaux à la pastille rouge de la
 * carte : seule, elle ne permettait pas d'agir. Sans signal, **son chrono** (le temps d'échange en Dialogue,
 * au vert ; le temps depuis le dernier échange en Concentré, au violet, orange à 5 min, rouge à 10).
 *
 * `compact` (la carte réduite d'un élève qu'on ne regarde pas) : le signal n'est plus que son glyphe, sur la
 * pastille de sa couleur, comme l'ancienne pastille compacte ; ses mots vont au lecteur d'écran. Le chrono, court,
 * garde sa chip.
 *
 * La chip du livre (`<Badge>`) ; les mots sont ceux des anciennes infobulles (`LiveParticipantIndicator`),
 * et `tutorStudentFaces` les tient.
 */
import React, { useRef } from "react"
import { View } from "react-native"

import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { Badge } from "../../atoms/Badge"
import { badgeFace } from "../../atoms/Badge/badgeFace"
import { CHIP_HEIGHT } from "../../atoms/Badge/Badge"
import { Blink } from "../../atoms/Blink"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { useSecondClock } from "../../hooks/useSecondClock"
import {
    CHRONO_SECONDS_BELOW,
    tutorStudentChronoChip,
    tutorStudentChronoHeld,
    tutorStudentChronoLabel,
    tutorStudentSignalChip,
} from "./tutorStudentFaces"

export type TutorStudentChipsProps = {
    student: UITutorLiveStudent
    /** Le signal en glyphe seul (une carte réduite). Défaut `false`. */
    compact?: boolean
    testID?: string
}

export function TutorStudentChips(props: TutorStudentChipsProps) {
    const { student, compact = false, testID } = props
    const signal = tutorStudentSignalChip(student)
    if (signal == null) return <ChronoChip student={student} testID={testID} />
    const { alert, ...chip } = signal
    const face = badgeFace(chip.variant, chip.tinted)
    const badge =
        compact && chip.iconName != null ? (
            <View accessible accessibilityLabel={chip.text} testID={testID}>
                <IconBubble iconName={chip.iconName} size={CHIP_HEIGHT} color={face.bg} iconColor={face.ink} />
            </View>
        ) : (
            <Badge {...chip} truncate={false} testID={testID} />
        )
    return alert === true ? <Blink>{badge}</Blink> : badge
}

/**
 * Le chrono, seul à avancer à la seconde (weekly du 03/11, W9) : sous 10 min, il dit ses secondes et relit
 * l'heure ; au-delà, ses minutes, et le relevé de la salle suffit. Rien d'autre de la carte ne se redessine.
 */
function ChronoChip(props: { student: UITutorLiveStudent; testID?: string }) {
    const { student, testID } = props
    const { kind, seconds: counted, runningSince } = student.chrono
    const since = runningSince != null && counted < CHRONO_SECONDS_BELOW ? runningSince : null
    const now = useSecondClock(since)
    const shown = useRef<{ id: string; kind: string; seconds: number } | null>(null)
    const live = since != null ? Math.max(counted, Math.floor((now - since) / 1000)) : counted
    const seconds = tutorStudentChronoHeld(shown.current, student.id, kind, live)
    shown.current = { id: student.id, kind, seconds }
    const chrono = tutorStudentChronoChip(student, seconds)
    return (
        <View accessible accessibilityLabel={`${tutorStudentChronoLabel(student)} : ${chrono.text}`} testID={testID}>
            <Badge {...chrono} truncate={false} />
        </View>
    )
}
